/**
 * Notifications wrapper
 **/
import {compose} from "mu-lambda";
import * as React from "react";

import Log, {Errors} from "@khan/logging";

import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "#data-access/gql/with-apollo.tsx";
import {eagerUpdateProfileCache} from "#data-access/user-profile-provider.tsx";
import withUserProfile, {
    type WithoutUserProfile,
    type WithUserProfileProps,
} from "#data-access/with-user-profile.tsx";

import type {
    clearBrandNewNotifications as GqlClearBrandNewNotifications,
    clearBrandNewNotificationsVariables as GqlClearBrandNewNotificationsVariables,
} from "./__graphql-types__/clearBrandNewNotifications.ts";
import type {
    getNotificationsForUser as GqlNotificationsForUser,
    getNotificationsForUserVariables as GqlNotificationsForUserVariables,
} from "./__graphql-types__/getNotificationsForUser.ts";
import {
    CLEAR_NOTIFICATIONS_MUTATION,
    NOTIFICATIONS_FOR_USER_QUERY,
} from "./notifications-queries.ts";

// Type added for fixture testing
export type NotificationsContextType = Omit<
    WithNotificationsProps,
    "notificationsLoading" | "notificationsCountLoading"
>;

// Context added for fixture testing
export const NotificationsContext: React.Context<
    NotificationsContextType | null | undefined
> = React.createContext<NotificationsContextType | null | undefined>(null);
NotificationsContext.displayName = "NotificationsContext";

/**
 * API calls used internally
 * TODO(WEB-1597): Remove this after creating GraphQL queries
 */

type Props = Readonly<
    {
        /**
         * The child component to render.
         */
        children: (props: WithNotificationsProps) => React.ReactNode;
    } & WithUserProfileProps &
        WithApolloProps
>;

type State = Omit<
    WithNotificationsProps,
    "loadNotifications" | "clearNotifications"
> & {
    // Whether we have loaded notifications
    everLoaded: boolean;
    // Previous cursor from the query/endpoint (used for paginating results)
    lastCursor: string | null | undefined;
    // The ID of the previous query for notifications, used for pagination
    lastId: string | null | undefined;
};

/**
 * InnerNotificationsProvider is responsible for these things:
 *
 * 1. Providing a function to load the readable notifications.
 * 2. Keeping a reference of the loaded readable notifications for a logged-in
 *    user.
 * 3. Keeping a reference of the current new notifications count for a logged-in
 *    user
 * 4. Clearing the notifications count.
 *
 * @private
 */
class InnerNotificationsProvider extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            everLoaded: false,
            hasMoreNotifications: true,
            lastCursor: null,
            lastId: null,
            notificationsLoading: false,
            notifications: [],
            notificationsCountLoading: false,
            newNotificationsCount: props.profile
                ? props.profile.get("countBrandNewNotifications")
                : 0,
        };
    }

    componentDidUpdate(prevProps: Props) {
        const {profile} = this.props;
        if (prevProps.profile !== profile) {
            /**
             * As per the React documentation, it is perfectly fine to call
             * setState from componentDidUpdate as long as it is guarded
             * with a condition that does not rely on the state being set.
             *
             * https://reactjs.org/docs/react-component.html#componentdidupdate
             * > You may call setState() immediately in componentDidUpdate()
             * > but note that it must be wrapped in a condition, or you’ll
             * > cause an infinite loop.
             */

            this.setState({
                newNotificationsCount: profile
                    ? profile.get("countBrandNewNotifications")
                    : 0,
            });
        }
    }

    _loadGqlNotifications = () => {
        const {lastId, hasMoreNotifications, notificationsLoading} = this.state;
        const {gqlClient} = this.props;

        // prevent loading twice the same data
        if (notificationsLoading || !hasMoreNotifications) {
            return;
        }

        this.setState({
            notificationsLoading: true,
            everLoaded: true,
        });

        const variables: GqlNotificationsForUserVariables = {
            after: lastId,
        };

        gqlClient
            .query<GqlNotificationsForUser, GqlNotificationsForUserVariables>(
                NOTIFICATIONS_FOR_USER_QUERY,
                {
                    fetchPolicy: "network-only",
                    variables: variables,
                },
            )
            .then(async (result) => {
                const resultNotificationContainer =
                    result.data?.user?.notifications;

                if (resultNotificationContainer) {
                    const currentNotifications = this.state.notifications;
                    const newNotifications: WithNotificationsProps["notifications"] =
                        [];

                    if (resultNotificationContainer.notifications) {
                        // NOTE: Notification Factory is quite large, we only
                        // import it when we need it.
                        const {getNotification} = await import(
                            "./notification-factory.ts"
                        );
                        for (const notification of resultNotificationContainer.notifications) {
                            const notificationObj =
                                await getNotification(notification);
                            if (notificationObj) {
                                newNotifications.push(notificationObj);
                            }
                        }
                    }

                    this.setState({
                        notifications:
                            currentNotifications.concat(newNotifications),
                        lastId: resultNotificationContainer.pageInfo
                            ?.nextCursor,
                        hasMoreNotifications:
                            resultNotificationContainer.pageInfo?.nextCursor !=
                            null,
                        notificationsLoading: false,
                    });
                }
            })
            .catch((error) => {
                Log.error("fetchNotificationsAPI error:", Errors.Internal, {
                    cause: error,
                });

                this.setState({
                    notificationsLoading: false,
                });
            });
    };

    /**
     * Load new/more notifications
     */
    loadNotifications = () => {
        this._loadGqlNotifications();
    };

    /**
     * Call API to clear the new notifications count
     */
    clearNotifications = () => {
        const {gqlClient} = this.props;

        if (this.state.newNotificationsCount === 0) {
            return;
        }

        this.setState({
            notificationsCountLoading: true,
        });

        // clear brand-new notifications from server
        gqlClient
            .mutate<
                GqlClearBrandNewNotifications,
                GqlClearBrandNewNotificationsVariables
            >(CLEAR_NOTIFICATIONS_MUTATION)
            .then((results) => {
                if (results.data?.clearBrandNewNotifications?.error) {
                    Log.error(
                        "clearNotificationsAPI error: " +
                            results.data?.clearBrandNewNotifications?.error
                                ?.code,
                        Errors.Internal,
                    );

                    this.setState({
                        notificationsCountLoading: false,
                    });
                } else {
                    // update notifications counter
                    // Since our UI is driven by profile data, and the
                    // profile data is on a 10-minute timer, we need
                    // to manually update the profile here.  Because
                    // doing a profile refresh currently has unwanted
                    // side effects, we'll update the client side
                    // update instead of a re-fetch.
                    eagerUpdateProfileCache(gqlClient, {
                        newNotificationCount: 0,
                    });

                    this.setState({
                        notificationsCountLoading: false,
                        newNotificationsCount: 0,
                    });
                }
            })
            .catch((error) => {
                Log.error("clearNotificationsAPI error:", Errors.Internal, {
                    cause: error,
                });

                this.setState({
                    notificationsCountLoading: false,
                });
            });
    };

    render(): React.ReactNode {
        const {children} = this.props;
        const {
            notificationsLoading,
            hasMoreNotifications,
            notifications,
            notificationsCountLoading,
            newNotificationsCount,
        } = this.state;

        return (
            <NotificationsContext.Consumer>
                {(mockContext?: NotificationsContextType | null) => {
                    return children({
                        notificationsLoading,
                        notificationsCountLoading,
                        notifications:
                            mockContext && mockContext.notifications
                                ? mockContext.notifications
                                : notifications,
                        hasMoreNotifications: mockContext
                            ? mockContext.hasMoreNotifications
                            : hasMoreNotifications,
                        loadNotifications:
                            mockContext && mockContext.loadNotifications
                                ? mockContext.loadNotifications
                                : this.loadNotifications,
                        newNotificationsCount:
                            mockContext && mockContext.newNotificationsCount
                                ? mockContext.newNotificationsCount
                                : newNotificationsCount,
                        clearNotifications:
                            mockContext && mockContext.clearNotifications
                                ? mockContext.clearNotifications
                                : this.clearNotifications,
                    });
                }}
            </NotificationsContext.Consumer>
        );
    }
}

/**
 * Provides internal access to the notification's info.
 *
 * This component uses `withUserProfile` to get the initial notification count
 * from the user profile data. If the user is logged out, the count will be set
 * to 0.
 *
 * @private
 */
const NotificationsProvider = compose(
    withUserProfile,
    withApollo,
)(InnerNotificationsProvider) as React.ComponentType<
    WithoutUserProfile<
        WithoutApollo<React.ComponentProps<typeof InnerNotificationsProvider>>
    >
>;

/**
 * This HOC injects the readable notifications and also injects a reference to
 * the `loadNotifications` function to the wrapped component.
 *
 * It also injects the notifications count as a `newNotificationsCount` prop and
 * also passes a reference to the `clearNotifications` function to the wrapped
 * component.
 *
 * This HOC assumes that your component is using exact props. Please update
 * your component to use exact props before using this HOC.
 *
 * Examples:
 * - export default withNotifications(WrappedComponent);
 * - export default compose(
 *      withNotifications,
 *      withRouter
 *   )(WrappedComponent);
 */

export type WithNotificationsProps = {
    // Set to true when SSR-ing or when waiting for notifications data on the
    // client.
    notificationsLoading: boolean;
    // The readable notifications list.
    // TODO(WEB-1597): Refactor this type after defining the GraphQL schema
    notifications: Array<any>;
    // Whether we have more notifications to load
    hasMoreNotifications: boolean;
    // Request more notifications from the server
    loadNotifications: () => unknown;
    // Set to true when SSR-ing or when waiting for new notifications count data
    // on the client.
    notificationsCountLoading: boolean;
    // pass current notification count to the child element
    newNotificationsCount: number;
    // clear new notifications
    clearNotifications: () => unknown;
};

export type WithoutNotifications<T> = Omit<T, keyof WithNotificationsProps>;

export default function withNotifications<
    C extends React.ComponentType<
        React.ComponentProps<C> & WithNotificationsProps
    > & {
        displayName?: string | undefined;
    },
    OuterProps = Omit<
        JSX.LibraryManagedAttributes<C, React.ComponentProps<C>>,
        keyof WithNotificationsProps
    >,
>(WrappedComponent: C): React.ComponentType<OuterProps> {
    const ComponentWithNotifications = (props: OuterProps) => (
        <NotificationsProvider>
            {({
                clearNotifications,
                notifications,
                hasMoreNotifications,
                loadNotifications,
                newNotificationsCount,
                notificationsLoading,
                notificationsCountLoading,
            }) => (
                <WrappedComponent
                    {...(props as any)}
                    notifications={notifications}
                    hasMoreNotifications={hasMoreNotifications}
                    loadNotifications={loadNotifications}
                    newNotificationsCount={newNotificationsCount}
                    clearNotifications={clearNotifications}
                    notificationsLoading={notificationsLoading}
                    notificationsCountLoading={notificationsCountLoading}
                />
            )}
        </NotificationsProvider>
    );

    // Try to create a nice displayName for React Dev Tools.
    const displayName =
        WrappedComponent.displayName || WrappedComponent.name || "Component";

    ComponentWithNotifications.displayName = `withWithNotifications(${displayName})`;

    return ComponentWithNotifications;
}
